<template>
  <div class="video-session-panel">

    <div id="video-content" class="row content" @error="errorHandler">

      <!-- Stream of local user -->
      <div
        class="video-local"
        :class="{ 'moderator': localUserIsModerator(), 'visitor': !localUserIsModerator() }"
      >
        <div class="badge-container">
          <img v-if="publisher && properties.hasVideo && isVideoOn" class="badge small-badge" :src="getUserIcon(localUser)"/>
          <div v-if="publisher && properties.hasVideo && isVideoOn" id="local-user-small-audio-badge" class="badge audio-badge small-badge" :style="{'background-color':getUserColor(localUser)}"/>
          <span v-if="localUserIsModerator()" class="moderator-title">Moderator:</span>
          <span class="user-name">{{localUserName}}</span>
        </div>


        <FluxComPublisher where="video-session" :opts="videoProperties()" />
        <div v-if="!publisher || !properties.hasVideo || !isVideoOn">
          <img class="badge central-badge normal-badge large-badge" :src="getUserIcon(localUser)"/>
          <div id="local-user-badge" class="badge central-badge audio-badge large-badge" :style="{'background-color':getUserColor(localUser)}"/>
        </div>
      </div>

      <!-- Streams of other users -->
      <div
        class="video-remote"
        v-for="stream in streams"
        @mouseenter="handleHoverOverStream(stream.connection.data.user_id, true)"
        @mouseleave="handleHoverOverStream(stream.connection.data.user_id, false)"
        :key="stream.streamId"
        :id="createElementId('stream', stream.connection.data.user_id)"
        :class="{'moderator': isUserWithIDModerator(stream.connection.data.user_id),'visitor': !isUserWithIDModerator(stream.connection.data.user_id)}"
      >
        <span class="badge-container">
          <img v-if="stream.hasVideo && getUserIcon(getUserOfId(stream.connection.data.user_id))"
               class="badge small-badge"
               :src="getUserIcon(getUserOfId(stream.connection.data.user_id))"/>
          <div v-if="stream.hasVideo && !getUserIcon(getUserOfId(stream.connection.data.user_id))
                      && getUserColor(getUserOfId(getUserOfId(stream.connection.data.user_id)))
                      && getUserOfId(stream.connection.data.user_id).getAcronym"
               class="badge central-badge audio-badge large-badge"
               :style="{'background-color':getUserColor(getUserOfId(stream.connection.data.user_id))}">
                    {{getUserOfId(stream.connection.data.user_id).getAcronym}}
          </div>
          <div v-if="stream.hasVideo" :id="createElementId('audio', stream.connection.data.user_id)" class="badge audio-badge small-badge" :style="{'background-color':getUserColor(getUserOfId(stream.connection.data.user_id))}"/>
          <span
            v-if="isUserWithIDModerator(stream.connection.data.user_id)"
            class="moderator-title"
          >Moderator:</span>
          <span class="user-name">{{getUserName(stream.connection.data.user_id)}}</span>
        </span>
        <FluxComSubscriber v-if="session && session.connection" :stream="stream" :opts="videoProperties(stream.connection.data.user_id)"></FluxComSubscriber>
        <div v-if="!stream.hasVideo">
          <img class="badge central-badge normal-badge large-badge" :src="getUserIcon(getUserOfId(stream.connection.data.user_id))"/>
          <div :id="createElementId('badge', stream.connection.data.user_id)" class="badge central-badge audio-badge large-badge" :style="{'background-color':getUserColor(getUserOfId(stream.connection.data.user_id))}"/>
        </div>

        <!-- Controls for streams -->
        <transition name="fade">
          <div class="subscriber-control-bar-container" v-if="help && getHoverStateOfStream(stream.connection.data.user_id)">
            <div class="volume-slider-container"
                 v-if="help && getHoverStateOfVolume(stream.connection.data.user_id)"
                 @mouseenter="handleHoverOverVolume(stream.connection.data.user_id, true)"
                 @mouseleave="handleHoverOverVolume(stream.connection.data.user_id, false)">
              <input class="subscriber-volume-slider"
                     type="range"
                     :value="getVolumeOfSubscriber(stream.connection.data.user_id)"
                     @change="setVolumeOfSubscriber(stream.connection.data.user_id)"
              >
            </div>
            <div class="subscriber-control-bar">
              <div class="volume-container"
                   @mouseenter="handleHoverOverVolume(stream.connection.data.user_id, true)"
                   @mouseleave="handleHoverOverVolume(stream.connection.data.user_id, false)">
                <md-button
                    v-if="help && getVolumeOfSubscriber(stream.connection.data.user_id) > 0"
                    @click="setVolumeOfSubscriber(stream.connection.data.user_id, 'mute')">
                  <VolumeHigh></VolumeHigh>
                </md-button>
                <md-button v-else
                           @click="setVolumeOfSubscriber(stream.connection.data.user_id, 'unmute')">
                  <VolumeOff></VolumeOff>
                </md-button>
              </div>
            </div>
          </div>
        </transition>
        
      </div>

      <!-- Stream panels of users that have neither audio nor video stream -->
      <div
          class="video-remote"
          v-for="user in usersWithoutStream"
          :key="user.id"
          :class="{'moderator': isUserWithIDModerator(user.id) ,'visitor': !isUserWithIDModerator(user.id)}"
      >
        <span class="name-badge">
          <span
              v-if="isUserWithIDModerator(user.id)"
              class="moderator-title"
          >Moderator:</span>
          <span class="user-name">{{user.name}}</span>
        </span>
        <img class="badge central-badge normal-badge large-badge" v-if="getUserIcon(user)" :src="getUserIcon(user)" alt="user-badge"/>
        <div v-if="!getUserIcon(user)
                      && getUserColor(user)
                      && getUserOfId(user).getAcronym"
             class="badge central-badge audio-badge large-badge"
             :style="{'background-color':getUserColor(getUserOfId(user))}">
          {{getUserOfId(stream.connection.data.user_id).getAcronym}}
        </div>
      </div>

    </div>

    <!-- Video Controls -->
    <div id="video-control" class="row footer">
      <FluxComVideoControls :publisher="publisher"></FluxComVideoControls>
    </div>

  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import MediaManager from "@/api/MediaManager";
import VolumeHigh from 'vue-material-design-icons/VolumeHigh.vue'
import VolumeOff from 'vue-material-design-icons/VolumeOff.vue'

const publisherDefaultBackground = require("@/assets/black_background_for_publisher.png");

const errorHandler = (err) => {
  console.error(err);
  alert(err.message);
};

export default {
  components: {VolumeHigh, VolumeOff},
  data: () => ({
    enlargeSidebar : false,
    showAudioVideoSettings: false,
    layoutSizes: "", //"md-layout-item md-size-50",
    publisherAndSubscriberProperties: {
      width: 160,
      height: 120,
      fitMode: "contain",
      frameRate: 15,
      insertDefaultUI: true,
      resolution: "320x240",
      showControls: false,
      style: {
        audioLevelDisplayMode: "off",
        buttonDisplayMode: "off",
        nameDisplayMode: "off",
        backgroundImageURI: publisherDefaultBackground,
      },
    },
    publisher: null,
    properties: {
      hasAudio: true,
      hasVideo: true
    },
    movingAvg: null,
    subscribers: [], //objects with the following properties: id, subscriber, volume, hoverOverStream, hoverOverVolume, movingAvg
    showControls: false,
    help: false,  // used to re-render when subscriberPanelHover values are changed
  }),
  computed: {
    ...mapState({
      isAdjustingSettings: (state) => state.session.isAdjustingSettings,
      isOnboardingComplete: (state) => state.session.isOnboardingComplete,
      openTokStreams: (state) => state.session.openTokStreams,
      isOpenTokSessionConnnected: (state) => state.session.isOpenTokSessionConnnected,
      session: (state) => state.session.openTokSession,
    }),
    ...mapGetters(["isInSetup","localUser","users","localUserID","userProfile",'isAudioOn','isVideoOn','getVideoDeviceId','scene']),
    localUserName() {
      return this.localUser.getName();
    },
    usersWithoutStream() {
      let list = [];
      for(let a = 0; a < this.users.length; a++){
        if(!this.hasUserStream(this.users[a].id) && this.users[a].id !== this.localUserID) {
          list.push(this.users[a]);
        }
      }
      return list;
    },
    streams() {
      return this.openTokStreams.filter((stream) => {return stream.videoType !== "screen"});
    }
  },
  mounted() {
    if(!this.isOnboardingComplete)
      this.showAudioVideoSettings = true;

    this.$root.$on('publisherCompleted', (publisher) => {
      this.publisher = publisher;
      this.publisher.on('audioLevelUpdated', (event) => {
        this.handleAudioLevelUpdatedEventForPublisher(event);
      });

      // Listen to followModerator command
      let lastTimeout;
      this.session.on("signal:followModerator", (event) => {
        clearTimeout(lastTimeout);

        let data = event.data.split(" ");
        let userID = data[0];
        let alpha = parseFloat(data[1]);
        let beta = parseFloat(data[2]);
        let situationID = data[3];
        if (userID != this.localUserID){
          let camera = window.scene.getCameraByName("MainCamera");
          camera.detachControl();
          this.setBlockSitutationTransition(true);
          
          this.handleGotoSituationTransition({situationID: situationID, comesFromModerator: true}); 
        
          setTimeout( () => {
            camera.alpha = alpha; 
            camera.beta = beta; 
          }, 200);

          lastTimeout = setTimeout(() => {
            this.setBlockSitutationTransition(false);
            this.scene.getCameraByName("MainCamera").attachControl(document.getElementById("renderCanvas"), true, false,4);
          }, 500);
        }
      });

      // Listen to Mute Signal to stop audio publish
      this.session.on("signal:mute", (event) => {
        console.log(event);
        if (event.data === this.localUserID){
          console.log(self.publisher);
          this.publisher.publishAudio(false);
          this.$store.commit("setAudioOn", false);
        }
      });

      // Listen to stopVideo Signal to stop audio publish
      this.session.on("signal:stopVideo", (event) => {
        console.log(event);
        if (event.data === this.localUserID){
          console.log(this.publisher);
          this.publisher.publishVideo(false);
          this.$store.commit("setVideoOn", false);
        }
      });

    });

    this.subscriberMovingAvg = new Map();
    this.$root.$on('subscriberConnected', (subscriber) => {
      let sameSubscriber = this.subscribers.filter((s) => {
        return s.id === subscriber.stream.connection.data.user_id
      });
      if(sameSubscriber.length < 1){
        this.$set(this.subscribers, this.subscribers.length, {
          id: subscriber.stream.connection.data.user_id,
          subscriber: subscriber,
          volume: subscriber.getAudioVolume(),
          hoverOverStream: false,
          hoverOverVolume: false,
          movingAvg: 0
        });
      }
      this.addAudioLevelListenerForSubscriber(subscriber);
    });
    this.session.on('streamPropertyChanged', (event) => {
      this.handleStreamPropertyChangedEvent(event);
    });
  },
  destroyed() {
    this.$root.$off('publisherCompleted', (publisher) => {
      this.publisher = publisher;
      this.addAudioLevelListenerForPublisher();
    });
    this.$root.$off('subscriberConnected', (subscriber) => {
      this.subscriberMovingAvg.set(subscriber.stream.connection.data.user_id, 0);
      this.addAudioLevelListenerForSubscriber(subscriber);
    });

    if(this.publisher) 
      this.publisher.off();

    if(this.session) 
      this.session.off('streamPropertyChanged');
  },
  methods: {
    ...mapMutations(["handleGotoSituationTransition",'setBlockSitutationTransition']),
    getUserIcon( u ) {
      return MediaManager.getUserAvatar(u);
    },
    createElementId(prefix, id) {
      return (prefix+"_"+id);
    },
    getSubscriberWithID(id){
      return this.subscribers.filter((s) => {
        return s.id === id
      })[0];
    },
    setMovingAvgForSubscriber(id, avg){
      let sub = this.getSubscriberWithID(id);
      if(sub) sub.movingAvg = avg;
    },
    getMovingAvgForSubscriber(id){
      let sub = this.getSubscriberWithID(id);
      if(sub) return sub.movingAvg;
      return undefined;
    },
    setVolumeOfSubscriber(id, newState) {
      let s = this.subscribers.filter((s) => {
        return s.id === id
      })[0];
      if (s && newState) {
        if (newState === "mute") s.subscriber.subscribeToAudio(false);
        else s.subscriber.subscribeToAudio(true);
      } else {
        if (s && document.getElementsByClassName("subscriber-volume-slider")[0]) {
          let value = Number(document.getElementsByClassName("subscriber-volume-slider")[0].value);
          s.subscriber.setAudioVolume(value);
        }
      }
      this.toggleReRenderHelp();
    },
    getVolumeOfSubscriber(id) {
      let s = this.subscribers.filter((s) => {
        return s.id === id
      })[0];
      if (s) return s.subscriber.getAudioVolume();
      return 0;
    },
    handleHoverOverVolume(id, isHovering) {
      let s = this.subscribers.filter((s) => {
        return s.id === id
      })[0];
      if (s) s.subscriber.hoverOverVolume = isHovering;
      this.toggleReRenderHelp();
    },
    // returns true if the user hovers over the volume button (which mutes or unmutes a subscriber)
    getHoverStateOfVolume(id) {
      let s = this.subscribers.filter((s) => {
        return s.id === id
      })[0];
      if (s) return s.subscriber.hoverOverVolume;
      return false;
    },
    // returns true if the user hovers over a stream panel
    getHoverStateOfStream(id) {
      let s = this.subscribers.filter((s) => {
        return s.id === id
      })[0];
      if (s) return s.subscriber.hoverOverStream;
      return false;
    },
    handleHoverOverStream(id, isHovering){
      let s = this.subscribers.filter((s) => {
        return s.id === id
      })[0];
      if (s) s.subscriber.hoverOverStream = isHovering;
      this.toggleReRenderHelp();
    },
    // needed to start a re-render because the variables of the objects in the subscribers list aren't reactive
    toggleReRenderHelp() {
      this.help = false;
      this.help = true;
    },
    handleStreamPropertyChangedEvent: function(event){
      if (self.publisher && event.stream && event.stream.id === self.publisher.streamId){
        try{
          this.properties[event.changedProperty] = event.newValue;
        }catch (e) {
          console.error(e);
        }
      }
    },
    addAudioLevelListenerForSubscriber(subscriber){
      const self = this;
      let activity = null;
      subscriber.on('audioLevelUpdated', function(event){
        let now = Date.now();
        if (event.audioLevel > 0.2) {
          if (!activity) {
            activity = {timestamp: now, talking: false};
          } else if (activity.talking) {
            activity.timestamp = now;
          } else if (now- activity.timestamp > 1000) {
            // detected audio activity for more than 1s
            // for the first time.
            activity.talking = true;
            document.getElementById("stream_" + subscriber.stream.connection.data.user_id).style.order = "2";
          }
        } else if (activity && now - activity.timestamp > 3000) {
          // detected low audio activity for more than 3s
          if (activity.talking) {
            document.getElementById("stream_" + subscriber.stream.connection.data.user_id).style.order = "3";
          }
          activity = null;
        }
        if(document.getElementById("badge_" + subscriber.stream.connection.data.user_id) || document.getElementById("audio_" + subscriber.stream.connection.data.user_id)) {
          let movingAvg = self.getMovingAvgForSubscriber(subscriber.stream.connection.data.user_id);
          let badge;
          if(document.getElementById("badge_" + subscriber.stream.connection.data.user_id)){
            badge = document.getElementById("badge_" + subscriber.stream.connection.data.user_id);
          }else{
            badge = document.getElementById("audio_" + subscriber.stream.connection.data.user_id);
          }

          if (movingAvg === null || movingAvg <= event.audioLevel) {
            movingAvg = event.audioLevel;
          } else {
            movingAvg = 0.7 * movingAvg + 0.3 * event.audioLevel;
          }

          // 1.5 scaling to map the -30 - 0 dBm range to [0,1]
          let logLevel = (Math.log(movingAvg) / Math.LN10) / 1.5 + 1;
          if(document.getElementById("local-user-badge")){
            logLevel = Math.min(Math.max(logLevel, 0), 1) + 1;
          }else{
            logLevel = Math.min(Math.max(logLevel, 0), 1) + 0.5;
          }
          self.setMovingAvgForSubscriber(subscriber.stream.connection.data.user_id, movingAvg);
          badge.style.transform = "scale(" + logLevel + ", " + logLevel + ")";
        }
      });
    },
    handleAudioLevelUpdatedEventForPublisher: function(event) {
      const self = this;
      if(document.getElementById("local-user-badge") || document.getElementById("local-user-small-audio-badge")) {
        let badge;
        if(document.getElementById("local-user-badge")){
          badge = document.getElementById("local-user-badge");
        }else{
          badge = document.getElementById("local-user-small-audio-badge");
        }

        if (self.movingAvg === null || self.movingAvg <= event.audioLevel) {
          self.movingAvg = event.audioLevel;
        } else {
          self.movingAvg = 0.7 * self.movingAvg + 0.3 * event.audioLevel;
        }

        // 1.5 scaling to map the -30 - 0 dBm range to [0,1]
        let logLevel = (Math.log(self.movingAvg) / Math.LN10) / 1.5 + 1;
        if(document.getElementById("local-user-badge")){
          logLevel = Math.min(Math.max(logLevel, 0), 1) + 1;
        }else{
          logLevel = Math.min(Math.max(logLevel, 0), 1) + 0.5;
        }
        badge.style.transform = "scale(" + logLevel + ", " + logLevel + ")";
      }
    },
    getUserColor(user){
      if(user) return ("#" + user.getColor().substring(2, 8));
      return "#ffffff";
    },
    hasUserStream(userId){
      for(let a = 0; a < this.openTokStreams.length; a++){
        if(this.openTokStreams[a].connection.data.user_id === userId){
          return true;
        }
      }
      return false;
    },
    getUserOfId(id){
      for(let a = 0; a < this.users.length; a++){
        if(this.users[a].id === id) return this.users[a]
      }
      return undefined;
    },
    errorHandler,
    isUserWithIDModerator(userID) {
      let user = this.users.filter((x) => x.getID() === userID);
      if (user.length > 0) {
        return user[0].getRole() === "moderator";
      }
      return false;
    },
    getUserName(userID) {
      let user = this.users.filter((x) => x.getID() === userID);
      if (user.length > 0) {
        return user[0].getName();
      }
      return "";
    },
    localUserIsModerator() {
      if (this.localUser && this.localUser.getRole() === "moderator")
        return true;
      return false;
    },
    videoProperties(id) {
      let vp = { ...this.publisherAndSubscriberProperties };
      // if id is not set, return properties of the local user
      let userID = id ? id : this.localUserID;
      if (this.isUserWithIDModerator(userID)) {
        vp.width = vp.width * 2;
        vp.height = vp.height * 2;
      }
      //console.log("Video properties for user " + id + " => " + JSON.stringify(vp));
      return vp;
    },
    
  },
}
</script>


<style scoped>
.video-session-panel{
  overflow-x: hidden;
}

.video-session-panel  .md-toolbar {
  height: 5%;
}

.hang-up {
  margin-right: 0;
}

.video-session-panel .visitor {
  width: 160px;
  max-height: 120px;
  order: 3;
}

.video-session-panel .video-local{
  order: 1 !important;
}

.video-session-panel .moderator {
  position: sticky;
  max-height: 244px;
  width: 326px;
  min-width: 326px;
  height: 246px;
  min-height: 246px;
  border-style: solid;
  border-color: var(--primaryColor);
  order: 0 !important;
}

.video-session-panel {
  display: flex;
  flex-flow: column;
  overflow-y: hidden;
  padding: 10px;
  background: #434346;
}

.video-session-panel .row {
  border: 0px dotted grey;
}

.video-session-panel .row.header {
  flex: 0 1 auto;
}

.video-session-panel .row.content {
  flex: 1 0 auto;
  margin-left: auto;
  margin-right: auto;
}

.video-session-panel .row.footer {
  padding: 0;
  flex: 0 1 30px;
}

.video-remote {
  position: relative;
  width: max-content;
  flex: 0 0 auto;
}
.video-remote.visitor, .video-local.visitor{
  background-color: #353535;
  min-height: 120px;
}

.video-local {
  position: relative;
  width: max-content;
}

.video-session-panel .badge-container {
  display: flex;
  align-items: flex-start;
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
}

.video-session-panel .moderator-title {
  color: var(--primaryColor);
}

.video-session-panel .user-name {
  color: white;
  font-weight: bold;
}

#video-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  overflow-x: auto;
  overflow-y: auto;
  height: calc(100% - 48px);
}

.video-session-panel ::-webkit-scrollbar{
  height: 13px;
}
.video-session-panel ::-webkit-scrollbar-track{
  background: rgb(49, 49, 49);
  border-radius: 10px;
}
.video-session-panel ::-webkit-scrollbar-thumb{
  border-radius:10px;
  border: 1px solid rgb(27, 27, 27);
  background: rgb(111,111,111);
}

#video-content ::-webkit-scrollbar{
  height: 13px;
}
#video-content ::-webkit-scrollbar-track{
  background: rgb(49, 49, 49);
  border-radius: 10px;
}
#video-content ::-webkit-scrollbar-thumb{
  border-radius:10px;
  border: 1px solid rgb(27, 27, 27);
  background: rgb(111,111,111);
}
.video-session-panel .name-badge{
  position: absolute;
  margin: 5px;
  z-index: 2;
}
.badge{
  border-radius: 100%;
}
.central-badge{
  position: absolute;
  top: calc(50% - 15px);
  left: calc(50% - 15px);
}
.normal-badge {
  z-index: 9;
}
.audio-badge {
  position: absolute;
  opacity: 0.6;
  z-index: 8 !important;
}
.large-badge{
  height: 30px;
  max-height: 30px;
  width: 30px;
  min-width: 30px;
}
.small-badge{
  z-index: 9;
  border-radius: 100%;
  height: 22px;
  max-height: 22px;
  width: 22px;
  min-width: 22px;
  margin-right: 5px;
}

.subscriber-control-bar-container {
  width: 100%;
  height: 100%;
  position: absolute;
  bottom: 0;
}

.subscriber-control-bar {
  position: absolute;
  display: flex;
  justify-content: flex-start;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.8);
  bottom: 0;
  right: 0;
  width: 100%;
}

.subscriber-control-bar .md-button {
  min-width: 20px; /* default value for md-button is 88px*/
  margin: 5px 0;
  border-radius: 0;
  width: 40px;
  height: 25px;
  max-width: 40px;
  max-height: 25px;
}

/* Source for input range element customization: https://css-tricks.com/styling-cross-browser-compatible-range-inputs-css/ */

/*** Start volume slider ***/

input[type=range] {
  -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
  width: 90%; /* Specific width is required for Firefox. */
  background: transparent; /* Otherwise white in Chrome */
}

input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none;
  border: 1px solid #000000;
  height: 24px;
  width: 8px;
  border-radius: 3px;
  background: #ffffff;
  cursor: pointer;
  margin-top: -9px; /* You need to specify a margin in Chrome, but in Firefox and IE it is automatic */
}

input[type=range]:focus {
  outline: none; /* Removes the blue border. You should probably do some kind of focus styling for accessibility reasons though. */
}

input[type=range]::-ms-track {
  width: 90%;
  cursor: pointer;
  height: 6px;

  /* Hides the slider so custom styles can be added */
  background: transparent;
  border-color: transparent;
  color: transparent;
}

/* All the same stuff for Firefox */
input[type=range]::-moz-range-thumb {
  border: 1px solid #000000;
  height: 24px;
  width: 8px;
  border-radius: 3px;
  background: #ffffff;
  cursor: pointer;
}

input[type=range]::-webkit-slider-runnable-track {
  width: 90%;
  height: 6px;
  cursor: pointer;
  background: var(--primaryColor);
}

input[type=range]:focus::-webkit-slider-runnable-track {
  background: var(--primaryColor);
}

input[type=range]::-moz-range-track {
  width: 90%;
  height: 6px;
  cursor: pointer;
  background: var(--primaryColor);
}

input[type=range]::-ms-fill-lower {
  background: var(--primaryColor);
}

input[type=range]::-ms-fill-upper {
  background: var(--primaryColor);
}

input[type=range]::-ms-thumb {
  border: 1px solid #000000;
  height: 24px;
  width: 8px;
  border-radius: 3px;
  background: #ffffff;
  cursor: pointer;
}

input[type=range]:focus::-ms-fill-lower {
  background: var(--primaryColor);
}

input[type=range]:focus::-ms-fill-upper {
  background: var(--primaryColor);
}

/*** End volume slider ***/

.volume-slider-container {
  display: flex;
  align-content: center;
  transform: rotate(-90deg);
  max-width: 90px;
  width: 85px;
  height: 34px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 0px 0px 4px 0px;
  -moz-border-radius: 0px 0px 4px 0px;

  position: absolute;
  justify-content: center;
  z-index: 2;
  left: -26px;
  bottom: 59px;
}

.volume-container {
  width: 50px;
}

.volume-container .md-button {
  width: 50px;
}
</style>

